/**
 * Border picker for the Configure pane. C-Type offers Print-to-edge | White
 * border; fine art adds a Handling border (width from the pricing borders
 * config, surfaced via borderOptionLabel). Borders do NOT change price —
 * this only sets what gets baked. An info box explains each mode (per the
 * iteration-1 feedback: "info box explaining white / handling / print-to-edge").
 * Framed prints render nothing (always print-to-edge).
 */

import { borderModesFor, type BorderMode, type PricingData } from '@rpl/pricing';
import { useState } from 'react';
import { borderInfo, borderModeLabel, borderOptionLabel } from '../../lib/sizePaper';
import { ModalShell } from './ModalShell';
import { Segmented } from './Segmented';

export interface BorderControlProps {
  pricing: PricingData;
  sizeId: string;
  framed: boolean;
  /** Current border; null = fine-art "not chosen yet" (Continue gate will force it). */
  value: BorderMode | null;
  onChange: (mode: BorderMode) => void;
}

export function BorderControl({ pricing, sizeId, framed, value, onChange }: BorderControlProps) {
  const modes = borderModesFor(sizeId, framed);
  const [open, setOpen] = useState(false);

  if (framed || modes.length <= 1) {
    return (
      <div className="cfg__field">
        <div className="eyebrow cfg__label">Border</div>
        <div className="bctl__framed">Framed prints always print to the frame edge.</div>
      </div>
    );
  }

  const isFineArt = modes.includes('handling');

  return (
    <div className="cfg__field">
      <div className="cfg__label-row">
        <div className="eyebrow cfg__label">Border</div>
        <button
          type="button"
          className="bctl__info-toggle"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          What’s this?
        </button>
      </div>

      <Segmented
        variant="light"
        className="bctl__seg"
        ariaLabel="Border style"
        segments={modes.map((m) => ({ key: m, label: borderOptionLabel(pricing, sizeId, m) }))}
        value={value}
        onChange={(key) => onChange(key as BorderMode)}
      />

      {value === null && isFineArt && (
        <div className="bctl__prompt">Choose a border before you continue.</div>
      )}

      {/* Owner 2026-07-27: a DIALOG, not an inline card. Expanding in place
          grew the pane and pushed the mobile step's summary chips off screen;
          an overlay can't disturb the layout at all. */}
      {open && (
        <ModalShell onClose={() => setOpen(false)} width={440} label="About borders" className="confirm">
          <div className="confirm__body">
            <h3 className="confirm__title">Borders</h3>
            <ul className="bctl__info">
              {modes.map((m) => (
                <li key={m} className="bctl__info-row">
                  <span className="bctl__info-name">{borderModeLabel(m)}</span>
                  <span className="bctl__info-text">{borderInfo(pricing, sizeId, m)}</span>
                </li>
              ))}
            </ul>
            <div className="confirm__actions">
              <button type="button" className="btn btn--primary" onClick={() => setOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
